import React, { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useApolloClient } from '@apollo/client';
import AsyncSelect from 'react-select/async';
import styled from 'styled-components';

import { useGroupRoster, useUser } from 'core/hooks';
import { EDUCATION_ROLES, visibleFor } from 'core/hooks/Palette/paletteRoles';
import {
  AllTeachersDocument,
  AllTeachersQuery,
  AllTeachersQueryVariables,
  PaginatedGroupsDocument,
  PaginatedGroupsQuery,
  PaginatedGroupsQueryVariables,
} from 'core/graphql';
import { UserRole } from 'types';
import { Colors } from 'services/ColorService';

import { ITaskSubject, SUBJECT_KINDS, SubjectKind } from './taskBuckets';

interface IEntityOption {
  value: string;
  label: string;
}

interface ITaskSubjectPickerProps {
  value: ITaskSubject;
  onChange: (subject: ITaskSubject) => void;
  /** Current teacher label, so an already-set teacher shows without a search. */
  teacherLabel?: string;
  /** Current group label, so an already-set group shows without a search. */
  groupLabel?: string;
  disabled?: boolean;
}

/**
 * The task's SUBJECT control (spec 0014) — a kind toggle plus the picker that
 * kind needs. Shared by the create modal and the details drawer so both speak
 * the same `TaskSubjectInput` shape.
 *
 * A task has exactly ONE subject, so switching the toggle replaces the whole
 * selection rather than layering a second link on top. That is also why the
 * caller sends `subject` and never the legacy flat `studentId`/`groupId` —
 * those can't express "and drop the previous kind".
 */
export const TaskSubjectPicker = memo(
  ({
    value,
    onChange,
    teacherLabel,
    groupLabel,
    disabled = false,
  }: ITaskSubjectPickerProps) => {
    const { t } = useTranslation();
    const apollo = useApolloClient();
    const [{ user }] = useUser();

    // `allTeachers` is gated to EDUCATION_ROLES on the backend — narrower than
    // the task board's own MANAGEMENT_ROLES. FinanceManager passes the task
    // gate but cannot search teachers, so offering them the kind would render
    // a picker that silently returns nothing for every query.
    const roles = useMemo(() => (user?.roles ?? []) as UserRole[], [user]);
    const canPickTeacher = visibleFor(roles, EDUCATION_ROLES);
    const kinds = useMemo(
      () =>
        SUBJECT_KINDS.filter((kind) => kind !== 'teacher' || canPickTeacher),
      [canPickTeacher],
    );

    // For kind='student' the group drives the roster, so read it from the
    // context field; for kind='group' the group IS the subject id.
    const rosterGroupId = value.kind === 'student' ? value.groupId : undefined;
    const { rows: roster } = useGroupRoster(rosterGroupId || undefined);

    const loadTeacherOptions = useCallback(
      async (input: string): Promise<IEntityOption[]> => {
        try {
          const result = await apollo.query<
            AllTeachersQuery,
            AllTeachersQueryVariables
          >({
            query: AllTeachersDocument,
            variables: { query: input || '', first: 20 },
            fetchPolicy: 'no-cache',
          });
          const edges = result.data?.allTeachers?.edges || [];
          return edges
            .map((edge) => edge?.node)
            .filter((node): node is NonNullable<typeof node> => Boolean(node))
            .map((node) => ({ value: node.id, label: node.fullName || '—' }));
        } catch {
          return [];
        }
      },
      [apollo],
    );

    // Searched, not a static list: the scoped boards (student / group / teacher
    // tabs) have no group list to hand down, and a static <select> there would
    // render an empty, dead-end control.
    const loadGroupOptions = useCallback(
      async (input: string): Promise<IEntityOption[]> => {
        try {
          const result = await apollo.query<
            PaginatedGroupsQuery,
            PaginatedGroupsQueryVariables
          >({
            query: PaginatedGroupsDocument,
            variables: { query: input || '', first: 20 },
            fetchPolicy: 'no-cache',
          });
          const edges = result.data?.allGroups?.edges || [];
          return edges
            .map((edge) => edge?.node)
            .filter((node): node is NonNullable<typeof node> => Boolean(node))
            .map((node) => ({ value: node.id, label: node.name }));
        } catch {
          return [];
        }
      },
      [apollo],
    );

    const rosterLabel = (row: typeof roster[number]): string => {
      const name =
        [row?.student?.user?.lastName, row?.student?.user?.firstName]
          .filter(Boolean)
          .join(' ') || '—';
      return row?.agreementTitle ? `${name} · ${row.agreementTitle}` : name;
    };

    // Switching kind drops the previous selection entirely — a half-filled
    // subject of the wrong kind would be rejected by the server anyway.
    const onKindChange = (kind: SubjectKind) => onChange({ kind });

    const teacherValue: IEntityOption | null =
      value.kind === 'teacher' && value.id
        ? { value: value.id, label: teacherLabel || '—' }
        : null;
    const groupValue: IEntityOption | null = value.id
      ? { value: value.id, label: groupLabel || '—' }
      : null;
    const studentGroupValue: IEntityOption | null = value.groupId
      ? { value: value.groupId, label: groupLabel || '—' }
      : null;

    return (
      <>
        <Field>
          <Label>{t('pages.tasks.subject.label')}</Label>
          <SelectInput
            value={value.kind}
            disabled={disabled}
            onChange={(e) => onKindChange(e.target.value as SubjectKind)}
          >
            {kinds.map((kind) => (
              <option key={kind} value={kind}>
                {t(`pages.tasks.subject.${kind}`)}
              </option>
            ))}
          </SelectInput>
        </Field>

        {value.kind === 'teacher' ? (
          <Field>
            <Label>{t('pages.tasks.subject.teacher')}</Label>
            <AsyncSelect
              cacheOptions
              defaultOptions
              isDisabled={disabled}
              value={teacherValue}
              onChange={(v) =>
                onChange({
                  kind: 'teacher',
                  id: (v as IEntityOption)?.value || undefined,
                })
              }
              loadOptions={loadTeacherOptions}
              placeholder={t('pages.tasks.subject.teacherPlaceholder')}
            />
          </Field>
        ) : null}

        {value.kind === 'group' ? (
          <Field>
            <Label>{t('pages.dashboard.col.group')}</Label>
            <AsyncSelect
              cacheOptions
              defaultOptions
              isDisabled={disabled}
              value={groupValue}
              onChange={(v) =>
                onChange({
                  kind: 'group',
                  id: (v as IEntityOption)?.value || undefined,
                })
              }
              loadOptions={loadGroupOptions}
              placeholder={t('pages.dashboard.pickGroup')}
            />
          </Field>
        ) : null}

        {value.kind === 'student' ? (
          <>
            <Field>
              <Label>{t('pages.dashboard.col.group')}</Label>
              <AsyncSelect
                cacheOptions
                defaultOptions
                isDisabled={disabled}
                value={studentGroupValue}
                onChange={(v) =>
                  // New group → the old roster row no longer applies.
                  onChange({
                    kind: 'student',
                    groupId: (v as IEntityOption)?.value || undefined,
                  })
                }
                loadOptions={loadGroupOptions}
                placeholder={t('pages.dashboard.pickGroup')}
              />
            </Field>
            <Field>
              <Label>{t('pages.tasks.drawer.student')}</Label>
              <SelectInput
                value={value.agreementId || ''}
                disabled={disabled || !value.groupId}
                onChange={(e) => {
                  const row = roster.find((r) => r?.id === e.target.value);
                  onChange({
                    kind: 'student',
                    groupId: value.groupId,
                    id: row?.student?.id,
                    agreementId: row?.id,
                  });
                }}
              >
                <option value="">{t('pages.tasks.subject.pickStudent')}</option>
                {roster.map((r) => (
                  <option key={r?.id} value={r?.id}>
                    {rosterLabel(r)}
                  </option>
                ))}
              </SelectInput>
            </Field>
          </>
        ) : null}
      </>
    );
  },
);

TaskSubjectPicker.displayName = 'TaskSubjectPicker';

const Field = styled.div`
  flex: 1;
  min-width: 0;
  margin-bottom: 12px;
`;

const Label = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${Colors.Gray};
  margin-bottom: 4px;
`;

const SelectInput = styled.select`
  width: 100%;
  box-sizing: border-box;
  border: 1px solid ${Colors.SecondLightGray};
  border-radius: 8px;
  padding: 8px;
  font: inherit;
  font-size: 13px;
  background: ${Colors.White};
`;
